/**
 * Mock report template data for MSP reporting prototype
 * Represents saved report configurations that can be reused
 */

export interface ReportTemplate {
  id: string;
  name: string;
  report_type: string;
  report_type_name: string;
  is_tenant_level: boolean; // true = shared with team, false = personal
  created_by: string;
  created_at: string;
  updated_at: string;
  configuration: {
    include_executive_summary: boolean;
    include_detailed_findings: boolean;
    include_remediation: boolean;
    delivery_method: 'download' | 'email' | 'storage';
    email_recipients?: string[];
    storage_destination?: string;
    default_date_range?: 'last_7_days' | 'last_30_days' | 'last_quarter' | 'last_year' | 'custom';
  };
  usage_count: number;
  last_used: string | null;
}

export const reportTemplatesMockData: ReportTemplate[] = [
  {
    id: 'rt-001',
    name: 'Monthly Healthcare HIPAA Compliance',
    report_type: 'HIPAA_COMPLIANCE',
    report_type_name: 'HIPAA Compliance Report',
    is_tenant_level: true,
    created_by: 'compliance@msp.com',
    created_at: '2025-02-15T10:00:00Z',
    updated_at: '2025-08-20T14:30:00Z',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: true,
      include_remediation: true,
      delivery_method: 'storage',
      storage_destination: 'SharePoint',
      default_date_range: 'last_30_days',
    },
    usage_count: 24,
    last_used: '2025-10-01T08:00:00Z',
  },
  {
    id: 'rt-002',
    name: 'Weekly Executive Brief',
    report_type: 'EXECUTIVE_REPORT',
    report_type_name: 'Executive Summary',
    is_tenant_level: true,
    created_by: 'admin@msp.com',
    created_at: '2025-03-10T11:00:00Z',
    updated_at: '2025-03-10T11:00:00Z',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: false,
      include_remediation: false,
      delivery_method: 'email',
      email_recipients: ['executives@msp.com'],
      default_date_range: 'last_7_days',
    },
    usage_count: 32,
    last_used: '2025-09-27T16:00:00Z',
  },
  {
    id: 'rt-003',
    name: 'NIST 800-171 Quarterly Review',
    report_type: 'NIST_800_171',
    report_type_name: 'NIST 800-171 Compliance Report',
    is_tenant_level: true,
    created_by: 'compliance@msp.com',
    created_at: '2025-04-01T09:00:00Z',
    updated_at: '2025-07-15T10:00:00Z',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: true,
      include_remediation: true,
      delivery_method: 'storage',
      storage_destination: 'Egnyte',
      default_date_range: 'last_quarter',
    },
    usage_count: 12,
    last_used: '2025-09-15T10:00:00Z',
  },
  {
    id: 'rt-004',
    name: 'Customer-Facing Monthly Service Review',
    report_type: 'MONTHLY_SERVICE_REVIEW',
    report_type_name: 'Monthly Service Review',
    is_tenant_level: true,
    created_by: 'admin@msp.com',
    created_at: '2025-01-15T10:00:00Z',
    updated_at: '2025-09-01T08:00:00Z',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'email',
      default_date_range: 'last_30_days',
    },
    usage_count: 45,
    last_used: '2025-10-01T09:00:00Z',
  },
  {
    id: 'rt-005',
    name: 'Critical Vulnerability Report',
    report_type: 'VULNERABILITY_REPORT',
    report_type_name: 'Vulnerability Report',
    is_tenant_level: false, // Personal template
    created_by: 'security@msp.com',
    created_at: '2025-05-15T10:00:00Z',
    updated_at: '2025-05-15T10:00:00Z',
    configuration: {
      include_executive_summary: false,
      include_detailed_findings: true,
      include_remediation: true,
      delivery_method: 'email',
      email_recipients: ['security-team@msp.com', 'management@msp.com'],
      default_date_range: 'last_7_days',
    },
    usage_count: 16,
    last_used: '2025-10-02T12:00:00Z',
  },
  {
    id: 'rt-006',
    name: 'Weekly SOC Activity Summary',
    report_type: 'SOC_EVENTS_REPORT',
    report_type_name: 'SOC Events Report',
    is_tenant_level: true,
    created_by: 'analyst@msp.com',
    created_at: '2025-01-20T08:00:00Z',
    updated_at: '2025-01-20T08:00:00Z',
    configuration: {
      include_executive_summary: false,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'download',
      default_date_range: 'last_7_days',
    },
    usage_count: 40,
    last_used: '2025-09-30T07:00:00Z',
  },
  {
    id: 'rt-007',
    name: 'Monthly SLA Performance Report',
    report_type: 'SLA_ADHERENCE_DATA_REPORT',
    report_type_name: 'SLA Adherence Report',
    is_tenant_level: true,
    created_by: 'admin@msp.com',
    created_at: '2025-02-10T13:00:00Z',
    updated_at: '2025-02-10T13:00:00Z',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'email',
      email_recipients: ['management@msp.com'],
      default_date_range: 'last_30_days',
    },
    usage_count: 28,
    last_used: '2025-10-05T14:00:00Z',
  },
  {
    id: 'rt-008',
    name: 'Simple General Security Snapshot',
    report_type: 'GENERAL_SECURITY',
    report_type_name: 'General Security Report',
    is_tenant_level: false,
    created_by: 'analyst@msp.com',
    created_at: '2025-06-01T09:00:00Z',
    updated_at: '2025-06-01T09:00:00Z',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: false,
      include_remediation: false,
      delivery_method: 'download',
      default_date_range: 'last_30_days',
    },
    usage_count: 8,
    last_used: '2025-09-20T11:00:00Z',
  },
  {
    id: 'rt-009',
    name: 'O365 Security Review',
    report_type: 'O365_ACCOUNT_ACTIVITY_REPORT',
    report_type_name: 'O365 Account Activity Report',
    is_tenant_level: false,
    created_by: 'analyst@msp.com',
    created_at: '2025-03-20T11:30:00Z',
    updated_at: '2025-03-20T11:30:00Z',
    configuration: {
      include_executive_summary: false,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'download',
      default_date_range: 'last_7_days',
    },
    usage_count: 18,
    last_used: '2025-09-24T10:00:00Z',
  },
  {
    id: 'rt-010',
    name: 'Escalated Cases Review',
    report_type: 'ESCALATED_CASES_REPORT',
    report_type_name: 'Escalated Cases Report',
    is_tenant_level: true,
    created_by: 'support@msp.com',
    created_at: '2025-04-15T10:00:00Z',
    updated_at: '2025-04-15T10:00:00Z',
    configuration: {
      include_executive_summary: false,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'download',
      default_date_range: 'last_30_days',
    },
    usage_count: 20,
    last_used: '2025-09-10T15:00:00Z',
  },
];

/**
 * Get tenant-level (shared) templates
 */
export const getTenantLevelTemplates = (): ReportTemplate[] => {
  return reportTemplatesMockData.filter((rt) => rt.is_tenant_level);
};

/**
 * Get personal templates for a user
 */
export const getPersonalTemplates = (userEmail: string): ReportTemplate[] => {
  return reportTemplatesMockData.filter((rt) => !rt.is_tenant_level && rt.created_by === userEmail);
};

/**
 * Get template by ID
 */
export const getTemplateById = (id: string): ReportTemplate | undefined => {
  return reportTemplatesMockData.find((rt) => rt.id === id);
};

/**
 * Get templates by report type
 */
export const getTemplatesByReportType = (reportType: string): ReportTemplate[] => {
  return reportTemplatesMockData.filter((rt) => rt.report_type === reportType);
};

/**
 * Get most used templates
 */
export const getMostUsedTemplates = (limit: number = 5): ReportTemplate[] => {
  return [...reportTemplatesMockData].sort((a, b) => b.usage_count - a.usage_count).slice(0, limit);
};
